import React, { useEffect, useState } from 'react';
import { Button, notification, Space, Table } from 'antd';
import type { TableProps } from 'antd';
import { IEmailFollowUp } from '@/interfaces/email-follow-up';
import { EditFilled, DeleteFilled, PlusOutlined } from '@ant-design/icons';
import {
  deleteEmailFollowUp,
  queryEmailFollowUps,
} from '@/adapters/email-follow-up';
import { useMutation } from '@tanstack/react-query';
import EmailFollowUpModal from './components/email-follow-up-modal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { IDataModal } from '@/interfaces/dataModal';
import Title from '@/components/title';
import SubMenu from '@/components/menu/sub-menu';
import { settingMenu } from '@/configs/menuItems';
import Card from '@/components/card';
import { getPathSegment } from '@/utils/pathUtils';
import { formatDate } from '@/utils/dataUtils';

const _emailFollowUpModal: IDataModal<IEmailFollowUp> = {
  open: false,
};

const EmailFollowUpSetting = () => {
  const pathSegment = getPathSegment();
  const [emailFollowUp, setEmailFollowUp] = useState<IEmailFollowUp[]>([]);
  const [dataModal, setDataModal] =
    useState<IDataModal<IEmailFollowUp>>(_emailFollowUpModal);
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<IEmailFollowUp>>(_emailFollowUpModal);
  const mutations = useMutation({
    mutationFn: async () => await queryEmailFollowUps(),
    mutationKey: ['fetchEmailFollowUp'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        setEmailFollowUp(data.data);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const fetchEmailFollowUp = async () => {
    try {
      mutations.mutateAsync();
    } catch (err) {
      console.error(err);
    }
  };

  const onActionEmailFollowUp = () => {
    const message = dataModal?.id
      ? 'อัพเดท Email Follow Up สำเร็จ'
      : 'สร้าง Email Follow Up สำเร็จ';
    notification.success({
      message: message,
    });
    setDataModal({ ..._emailFollowUpModal });
    fetchEmailFollowUp();
  };

  const onDeleteEmailFollowUp = async () => {
    await deleteEmailFollowUp(deleteModal?.id?.toString() || '');
    fetchEmailFollowUp();
    setDeleteModal({ ..._emailFollowUpModal });
  };

  useEffect(() => {
    fetchEmailFollowUp();
  }, []);

  const columns: TableProps<IEmailFollowUp>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      className: 'text-center',
      fixed: 'left',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'Country Code',
      dataIndex: 'countryCode',
      key: 'countryCode',
      className: 'font-medium',
    },
    {
      title: 'Country Name',
      dataIndex: 'countryName',
      key: 'countryName',
      className: 'font-medium',
    },
    {
      title: 'Email To.',
      dataIndex: 'to',
      key: 'to',
      className: 'font-medium',
    },
    {
      title: 'Email CC.',
      dataIndex: 'cc',
      key: 'cc',
      className: 'font-medium',
    },
    {
      title: 'Updated By',
      dataIndex: 'updateByName',
      key: 'updateByName',
      className: 'font-medium',
      align: 'center',
      width: 120,
    },
    {
      title: 'Updated Date',
      dataIndex: 'updateDate',
      key: 'updateDate',
      className: 'font-medium',
      width: 120,
      align: 'center',
      render: _ => formatDate(_),
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          <DeleteFilled
            className="text-red-700"
            onClick={() =>
              setDeleteModal({
                open: true,
                item: rec,
                id: rec.countryCode,
              })
            }
          />
          <EditFilled
            className="text-blue-500"
            onClick={() =>
              setDataModal({
                open: true,
                item: rec,
                id: rec.countryCode,
              })
            }
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <SubMenu active={pathSegment} items={settingMenu} />
      <Card>
        <Title title="Email Follow Up">
          <Button
            type="primary"
            className="max-w-[90px]"
            onClick={() => {
              setDataModal({
                ...dataModal,
                open: true,
              });
            }}
          >
            <PlusOutlined /> เพิ่มใหม่
          </Button>
        </Title>
        <div className="flex flex-col gap-3">
          <Table
            loading={mutations.isPending}
            rowKey={r => r.countryCode}
            columns={columns}
            dataSource={emailFollowUp || []}
            size="small"
            bordered
            className="font-ibm"
            pagination={{
              pageSize: 10,
            }}
            scroll={{ x: 1200 }}
          />
          <EmailFollowUpModal
            handleCancel={() => {
              setDataModal({ ..._emailFollowUpModal });
            }}
            handleOk={() => {
              onActionEmailFollowUp();
            }}
            dataModal={dataModal}
          />
          <DeleteConfirmModal
            content={`คุณต้องการลบ '${deleteModal?.item?.countryName}' ใช่หรือไม่`}
            handleCancel={() => {
              setDeleteModal({ ..._emailFollowUpModal });
            }}
            handleOk={() => {
              onDeleteEmailFollowUp();
            }}
            open={deleteModal?.open}
            title={`'${deleteModal?.item?.countryName}'`}
          />
        </div>
      </Card>
    </>
  );
};

export default EmailFollowUpSetting;
